import type { QueryReportItem } from '../../models/api/query/query-a1';

const WORKERS_COMP_LABEL = "Workers' Comp";
const DISABILITY_LABEL = 'Disability';

/** Maps OI Major Classification labels to legacy data_set codes. */
export function mapFriendlyLobToCode(value: string): string {
  const normalized = (value ?? '').trim().toUpperCase();
  switch (normalized) {
    case "WORKERS' COMP":
    case 'WORKERS COMP':
    case 'WORKERS_COMP':
    case 'WORKERSCOMP':
    case 'WC':
      return 'WC';
    case 'DISABILITY':
    case 'DS':
      return 'DS';
    case 'GENERAL LIABILITY':
    case 'GENERAL_LIABILITY':
    case 'GENERALLIABILITY':
    case 'GL':
      return 'GL';
    case 'AUTO LIABILITY':
    case 'AUTO_LIABILITY':
    case 'AUTOLIABILITY':
    case 'AU':
      return 'AU';
    case 'PROPERTY':
    case 'PROPERTY LIABILITY':
    case 'PROPERTY_LIABILITY':
    case 'PROPERTYLIABILITY':
    case 'PR':
      return 'PR';
    default:
      return '';
  }
}

function lobSetEquals(a: Set<string>, b: readonly string[]): boolean {
  if (a.size !== b.length) {
    return false;
  }
  return b.every((x) => a.has(x));
}

export type ReportLobRequirement = 'ds' | 'wc' | 'ds-or-wc';

/** User-facing snackbar / toast copy (Major Classification step). */
export function buildReportLobMismatchMessage(
  reportName: string,
  requirement: ReportLobRequirement,
): string {
  const name = (reportName ?? '').trim() || 'this report';
  switch (requirement) {
    case 'ds':
      return `To run ${name}, select ${DISABILITY_LABEL} under Major Classification.`;
    case 'wc':
      return `To run ${name}, select ${WORKERS_COMP_LABEL} under Major Classification.`;
    case 'ds-or-wc':
      return `To run ${name}, select ${WORKERS_COMP_LABEL} and/or ${DISABILITY_LABEL} under Major Classification.`;
  }
}

/**
 * Legacy SaveQueryPageController.VerifyLOBSelection (exact DS / WC / DS+WC data_set sets).
 * Returns the first error message, or null when valid.
 */
export function validateSelectedReportsLobCompatibility(
  selectedFriendlyLobs: string[],
  reports: Pick<QueryReportItem, 'reportId' | 'reportName' | 'linesOfBusiness'>[],
): string | null {
  const selectedCodes = new Set(
    (selectedFriendlyLobs ?? [])
      .map(mapFriendlyLobToCode)
      .filter((code) => !!code),
  );

  const dsOnly = ['DS'] as const;
  const wcOnly = ['WC'] as const;
  const dsWcPair = ['DS', 'WC'] as const;

  for (const report of reports) {
    const required = new Set(
      (report.linesOfBusiness ?? [])
        .map((code) => (code ?? '').trim().toUpperCase())
        .filter((code) => !!code),
    );

    if (required.size === 0) {
      continue;
    }

    const name = (report.reportName ?? '').trim() || `Report ${report.reportId}`;

    if (lobSetEquals(required, dsOnly) && !selectedCodes.has('DS')) {
      return buildReportLobMismatchMessage(name, 'ds');
    }

    if (lobSetEquals(required, wcOnly) && !selectedCodes.has('WC')) {
      return buildReportLobMismatchMessage(name, 'wc');
    }

    if (
      lobSetEquals(required, dsWcPair) &&
      !selectedCodes.has('DS') &&
      !selectedCodes.has('WC')
    ) {
      return buildReportLobMismatchMessage(name, 'ds-or-wc');
    }
  }

  return null;
}
